import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { fetchSession, sessionQueryKey } from '@/features/auth/use-session'

/**
 * Lands here after cwc-backend-v1's OAuth callback, mirroring cwc-frontend's
 * /confirm page (app/pages/confirm/index.vue) — the Set-Cookie already
 * happened server-side, this page's only job is to pick up the new session
 * and continue to wherever the login started from.
 */
const ERROR_MESSAGES: Record<string, string> = {
  'oauth-failed': 'Sign-in with the provider failed. Please try again.',
  'provider-disabled': 'This sign-in method is not available right now.',
  'rate-limited': 'Too many attempts. Please wait a moment and try again.',
  'account-disabled': 'This account is disabled. Contact an administrator.',
}

export function ConfirmPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    const errorCode = params.get('error')
    if (errorCode) {
      setErrorMessage(ERROR_MESSAGES[errorCode] ?? 'Sign-in failed. Please try again.')
      return
    }

    const redirect = params.get('redirect') || '/'

    queryClient
      .fetchQuery({ queryKey: sessionQueryKey, queryFn: fetchSession, staleTime: 0 })
      .then((user) => {
        if (!user) {
          setErrorMessage('You are not recognized as staff on this account.')
          return
        }
        navigate(decodeURIComponent(redirect), { replace: true })
      })
      .catch(() => setErrorMessage('Could not confirm your session. Please try again.'))
    // Runs once on mount — params/navigate/queryClient are stable for this page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (errorMessage) {
    return (
      <div className="grid h-full place-items-center p-6">
        <Card className="w-full max-w-sm text-center">
          <CardTitle>Sign-in failed</CardTitle>
          <CardDescription className="mt-2">{errorMessage}</CardDescription>
          <Button className="mt-5 w-full" onClick={() => navigate('/login', { replace: true })}>
            Back to sign in
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="grid h-full place-items-center text-sm text-muted-foreground">
      Signing you in…
    </div>
  )
}
